/** Sidebar + top bar + date navigator around the current page. */
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Calendar } from './components/Calendar';
import { Icon, type IconName } from './components/Icon';
import { config } from './config';
import { addDays, formatDayLong } from './lib/dates';
import { log } from './lib/logger';
import { useApp } from './state/app';

export type Route = 'dashboard' | 'diary' | 'foods' | 'cook' | 'body' | 'tef' | 'trends' | 'profile' | 'owner';

/** `owner`: listed only for the site owner's account (app_admins). */
export const ROUTES: ReadonlyArray<{ id: Route; label: string; icon: IconName; datePicker: boolean; owner?: boolean }> = [
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', datePicker: true },
  { id: 'diary', label: 'Diary', icon: 'diary', datePicker: true },
  { id: 'foods', label: 'Foods', icon: 'database', datePicker: false },
  { id: 'cook', label: 'Cook & batch', icon: 'flame', datePicker: true },
  { id: 'body', label: 'Body', icon: 'scale', datePicker: false },
  { id: 'tef', label: 'TEF & True Label', icon: 'tag', datePicker: true },
  { id: 'trends', label: 'Trends', icon: 'trend', datePicker: false },
  { id: 'profile', label: 'Targets & Profile', icon: 'target', datePicker: false },
  { id: 'owner', label: 'Owner dashboard', icon: 'shield', datePicker: false, owner: true },
];

const COMING: ReadonlyArray<{ label: string; icon: IconName }> = [];

type Theme = 'system' | 'light' | 'dark';
const THEME_KEY: string = 'mightypeanut.theme';

function readTheme(): Theme {
  try { const t = localStorage.getItem(THEME_KEY); return t === 'light' || t === 'dark' ? t : 'system'; } catch { return 'system'; }
}

export function Shell({ route, go, children }: { route: Route; go: (r: Route) => void; children: ReactNode }): ReactElement {
  const { source, day, setDay, today, profile, version, isAdmin, pendingReviews } = useApp();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [calOpen, setCalOpen] = useState<boolean>(false);
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [marked, setMarked] = useState<ReadonlySet<string>>(new Set());
  const calRef = useRef<HTMLDivElement>(null);
  const current = ROUTES.find((r) => r.id === route) ?? ROUTES[0]!;

  useEffect(() => {
    const root: HTMLElement = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme'); else root.setAttribute('data-theme', theme);
    try { if (theme === 'system') localStorage.removeItem(THEME_KEY); else localStorage.setItem(THEME_KEY, theme); } catch { /* storage unavailable */ }
  }, [theme]);

  useEffect(() => {
    if (!calOpen) return;
    let cancelled: boolean = false;
    source.listDailyMetrics(addDays(day, -45), addDays(day, 45))
      .then((rows) => { if (!cancelled) setMarked(new Set(rows.filter((r) => r.meal_count > 0).map((r) => r.day))); })
      .catch((err: unknown) => log.warn('calendar.markers_failed', { error: String(err) }));
    const onDoc = (e: MouseEvent): void => { if (calRef.current && !calRef.current.contains(e.target as Node)) setCalOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => { cancelled = true; document.removeEventListener('mousedown', onDoc); };
  }, [calOpen, source, day, version]);

  const isDark: boolean = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  return (
    <div className="app">
      <aside className={`sidebar ${menuOpen ? 'open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) setMenuOpen(false); }}>
        <div className="brand"><span className="brand-mark brand-mascot"><img src={`${import.meta.env.BASE_URL}peanut-128.png`} alt="" /></span>{config.appName}</div>
        <nav className="nav-group" aria-label="Main">
          <div className="nav-label">Track</div>
          {ROUTES.filter((r) => !r.owner).map((r) => (
            <a key={r.id} href={`#/${r.id}`} className={`nav-item ${route === r.id ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); go(r.id); setMenuOpen(false); }}>
              <Icon name={r.icon} size={19} />{r.label}
            </a>
          ))}
        </nav>
        {isAdmin && (
          <nav className="nav-group" aria-label="Owner">
            <div className="nav-label">Owner</div>
            {ROUTES.filter((r) => r.owner).map((r) => (
              <a key={r.id} href={`#/${r.id}`} className={`nav-item ${route === r.id ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); go(r.id); setMenuOpen(false); }}>
                <Icon name={r.icon} size={19} />{r.label}
                {pendingReviews !== null && pendingReviews > 0 && <span className="nav-count" title="Food submissions waiting for review">{pendingReviews}</span>}
              </a>
            ))}
          </nav>
        )}
        <nav className="nav-group" aria-label="Lifting">
          <div className="nav-label">Lifting</div>
          {/* Served from this same origin, so the session carries across and this is just a link. */}
          <a href="/lifting/" className="nav-item"><Icon name="dumbbell" size={19} />Workouts</a>
        </nav>
        {COMING.length > 0 && (
          <div className="nav-group">
            <div className="nav-label">Coming next</div>
            {COMING.map((c) => <div key={c.label} className="nav-item" aria-disabled="true"><Icon name={c.icon} size={19} />{c.label}<span className="pill">SOON</span></div>)}
          </div>
        )}
        <div className="side-foot">
          {source.kind === 'demo' && <div className="demo-badge">DEMO DATA</div>}
          <div className="email">{profile.display_name ?? source.userEmail}</div>
          <div style={{ color: 'var(--side-muted)', fontSize: 12 }}>{source.userEmail}</div>
          <div className="side-actions">
            <button type="button" className="side-btn" onClick={() => setTheme(isDark ? 'light' : 'dark')} aria-label="Toggle dark mode"><Icon name={isDark ? 'sun' : 'moon'} size={16} /></button>
            {!config.demoOnly && <button type="button" className="side-btn" onClick={() => void source.signOut().then(() => window.location.reload())}><Icon name="logout" size={16} /> {source.kind === 'demo' ? 'Exit demo' : 'Sign out'}</button>}
          </div>
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <button type="button" className="icon-btn menu-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu"><Icon name="menu" size={20} /></button>
          <h1>{current.label}</h1>
          <div className="spacer" />
          {current.datePicker && (
            <div className="datenav" ref={calRef}>
              <button type="button" className="icon-btn" onClick={() => setDay(addDays(day, -1))} aria-label="Previous day"><Icon name="chevL" size={18} /></button>
              <button type="button" className="date-label" onClick={() => setCalOpen((o) => !o)}>{day === today ? 'Today' : formatDayLong(day)}</button>
              <button type="button" className="icon-btn" onClick={() => setDay(addDays(day, 1))} aria-label="Next day"><Icon name="chevR" size={18} /></button>
              {calOpen && <div className="popover"><Calendar value={day} today={today} marked={marked} onPick={(d) => { setDay(d); setCalOpen(false); }} /></div>}
            </div>
          )}
        </header>
        <main className="content">{children}</main>
      </div>
    </div>
  );
}
